import { Fragment, type ReactNode } from 'react'
import { ToastProvider } from './ToastContext'
import { NotificationProvider } from './NotificationContext'
import { AiJobProvider } from './AiJobContext'
import { ToastViewport } from '../components/Toast/ToastViewport'
import { WorkspaceSwitchGuardSheet } from '../components/Common/WorkspaceSwitchGuardSheet'
import { WorkspaceSwitchCurtain } from '../components/Common/WorkspaceSwitchCurtain'
import { DataProvider } from './DataContext'
import { TimelineProvider } from './TimelineContext'
import { ManageDraftProvider } from './ManageDraftContext'
import { DashboardProvider } from './DashboardContext'
import { FocusModeProvider } from './FocusModeContext'
import { ChecklistDetailPopupProvider } from './ChecklistDetailPopupContext'
import { CanvasScaleProvider } from './CanvasScaleContext'
import { NavigationProvider } from './NavigationContext'
import { GamificationProvider } from './GamificationContext'
import { CosmeticPreferencesBridge } from './CosmeticPreferencesBridge'
import { FunPackThemeBridge } from './FunPackThemeBridge'
import { RaidProvider } from './RaidContext'
import { FocusHorizonProvider } from './FocusHorizonContext'
import { ChatProvider } from './ChatContext'
import { ExperienceEngineProvider } from './ExperienceEngineContext'
import { CarouselCacheProvider } from '../components/CinematicCarousel'
import { TourGuideProvider } from '../components/tourguide/TourGuideContext'

export type WorkspaceShell = 'member' | 'web' | 'helper'

export interface WorkspaceScopedProvidersProps {
  workspaceId: string
  children: ReactNode
  shell?: WorkspaceShell
  hasDataProvider?: boolean
  hasNotificationProvider?: boolean
  hasCanvasScale?: boolean
  hasRaid?: boolean
}

/**
 * WorkspaceScopedProviders (PD1, Phase S3)
 * Remounts all workspace-scoped providers under `<Fragment key={workspaceId}>`
 * while preserving session-level providers (Toast, Notifications, AiJobs,
 * and session-level overlays: ToastViewport, WorkspaceSwitchGuardSheet, WorkspaceSwitchCurtain)
 * above the key.
 */
export const WorkspaceScopedProviders = ({
  workspaceId,
  children,
  shell,
  hasDataProvider = shell !== 'helper',
  // Helper shell owns its notification listener via
  // subscribeToHelperNotifications inside HelperNotificationDrawer
  // (helperNotificationService.ts). NotificationProvider is
  // intentionally NOT mounted here — mounting it would open a
  // second onSnapshot on notifications/{uid}/items and no Helper
  // consumer reads useNotifications. Member shell keeps the
  // provider because MemberNotificationDrawer uses the hook.
  hasNotificationProvider = shell !== 'helper',
  hasCanvasScale = shell !== 'helper',
  hasRaid = shell !== 'helper',
}: WorkspaceScopedProvidersProps) => {
  // Inner core below the key
  const innerSubtree = (
    <FocusHorizonProvider>
      <ChatProvider>
        <ExperienceEngineProvider>
          <CarouselCacheProvider>
            <TourGuideProvider>
              {children}
            </TourGuideProvider>
          </CarouselCacheProvider>
        </ExperienceEngineProvider>
      </ChatProvider>
    </FocusHorizonProvider>
  )

  const raidWrapped = hasRaid ? (
    <RaidProvider>{innerSubtree}</RaidProvider>
  ) : (
    innerSubtree
  )

  const gamificationWrapped = (
    <NavigationProvider>
      <GamificationProvider>
        <CosmeticPreferencesBridge />
        <FunPackThemeBridge />
        {raidWrapped}
      </GamificationProvider>
    </NavigationProvider>
  )

  const canvasWrapped = hasCanvasScale ? (
    <CanvasScaleProvider>{gamificationWrapped}</CanvasScaleProvider>
  ) : (
    gamificationWrapped
  )

  const workspaceCore = (
    <TimelineProvider workspaceId={workspaceId}>
      <ManageDraftProvider>
        <DashboardProvider>
          <FocusModeProvider>
            <ChecklistDetailPopupProvider>
              {canvasWrapped}
            </ChecklistDetailPopupProvider>
          </FocusModeProvider>
        </DashboardProvider>
      </ManageDraftProvider>
    </TimelineProvider>
  )

  const belowKey = hasDataProvider ? (
    <DataProvider>{workspaceCore}</DataProvider>
  ) : (
    workspaceCore
  )

  // Session-level children mounted above the key
  const sessionChildren = (
    <>
      <ToastViewport />
      <WorkspaceSwitchGuardSheet />
      <WorkspaceSwitchCurtain />
      <Fragment key={workspaceId}>
        {belowKey}
      </Fragment>
    </>
  )

  const aiWithSession = <AiJobProvider currentWorkspaceId={workspaceId}>{sessionChildren}</AiJobProvider>

  return (
    <ToastProvider>
      {hasNotificationProvider ? (
        <NotificationProvider>{aiWithSession}</NotificationProvider>
      ) : (
        aiWithSession
      )}
    </ToastProvider>
  )
}
